import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Mail, User, Eye, EyeOff, ArrowRight, Shield, CheckCircle, Loader2, ArrowLeft, Send, Clock } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { toast } from 'sonner';
import { Logo } from '../../Logo';
import { authAPI, setAuthToken } from '../../../services/api';


type AuthView = 'login' | 'register' | 'forgot-password' | 'verify-forgot-otp' | 'reset-password';

export function AdminLoginPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [view, setView] = useState<AuthView>('login');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    
    // Login state
    const [loginData, setLoginData] = useState({
        email: '',
        password: ''
    });

    // Register state
    const [registerData, setRegisterData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
    });

    // Registration OTP state
    const [registrationOTPState, setRegistrationOTPState] = useState({
        otpSent: false,
        otp: '',
        email: ''
    });

    // Forgot password state
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
    
    // OTP state for forgot password (8 digits)
    const [otpData, setOtpData] = useState({
        email: '',
        otp: ['', '', '', '', '', '', '', ''], // 8 digits
        resetToken: ''
    });

    // Reset password state
    const [resetPasswordData, setResetPasswordData] = useState({
        password: '',
        confirmPassword: ''
    });



    // Handle login
    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!loginData.email || !loginData.password) {
            toast.error('Please fill in all fields');
            return;
        }

        setIsLoading(true);
        try {
            const response = await authAPI.login(loginData.email, loginData.password);
            
            // Save token and user data
            setAuthToken(response.token);
            localStorage.setItem('adminAuth', 'true');
            localStorage.setItem('adminUser', JSON.stringify(response.data));
            
            toast.success('Login Successful!', {
                description: `Welcome back, ${response.data.name}!`,
            });
            
            navigate('/admin');
        } catch (error: any) {
            console.error('Login error:', error);
            
            if (error.message.includes('pending Super Admin approval') || error.message.includes('pending') || error.message.includes('approval')) {
                toast.error('Registration Pending', {
                    description: 'Your registration is awaiting Super Admin approval.',
                    duration: 5000,
                });
            } else if (error.message.includes('locked')) {
                toast.error('Account Locked', {
                    description: 'Too many failed attempts. Try again later or reset your password.',
                });
            } else {
                toast.error('Login Failed', {
                    description: error.message || 'Invalid email or password.',
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Handle send OTP for registration
    const handleSendRegistrationOTP = async () => {
        // Validate form
        if (!registerData.name || !registerData.email || !registerData.password || !registerData.confirmPassword) {
            toast.error('Please fill in all fields');
            return;
        }

        if (registerData.password !== registerData.confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (registerData.password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }

        setIsLoading(true);
        try {
            const response = await authAPI.register({
                name: registerData.name,
                email: registerData.email,
                password: registerData.password,
                confirmPassword: registerData.confirmPassword
            });
            
            toast.success('OTP Sent!', {
                description: 'An OTP has been sent to the Super Admin. Please ask the Super Admin for the OTP.',
                duration: 6000,
            });
            
            setRegistrationOTPState({
                otpSent: true,
                otp: '',
                email: registerData.email
            });
        } catch (error: any) {
            toast.error('Failed to Send OTP', {
                description: error.message || 'Could not send OTP.',
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle resend registration OTP
    const handleResendRegistrationOTP = async () => {
        setIsLoading(true);
        try {
            await authAPI.register({
                name: registerData.name,
                email: registerData.email,
                password: registerData.password,
                confirmPassword: registerData.confirmPassword
            });
            
            toast.success('OTP Resent!', {
                description: 'A new OTP has been sent to the Super Admin.',
            });
            
            setRegistrationOTPState({
                ...registrationOTPState,
                otp: ''
            });
        } catch (error: any) {
            toast.error('Failed to Resend OTP', {
                description: error.message || 'Could not resend OTP.',
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle verify registration OTP
    const handleVerifyRegistrationOTP = async () => {
        if (!registrationOTPState.otp || registrationOTPState.otp.length !== 8) {
            toast.error('Please enter the 8-digit OTP');
            return;
        }

        setIsLoading(true);
        try {
            const response = await authAPI.verifyRegistrationOTP(
                registrationOTPState.email,
                registrationOTPState.otp
            );
            
            toast.success('Registration Successful!', {
                description: 'Your account has been created. Please login with your credentials.',
                duration: 5000,
            });
            
            // Reset registration state
            setRegisterData({
                name: '',
                email: '',
                password: '',
                confirmPassword: ''
            });
            setRegistrationOTPState({
                otpSent: false,
                otp: '',
                email: ''
            });
            
            // Redirect to login
            setView('login');
        } catch (error: any) {
            toast.error('Verification Failed', {
                description: error.message || 'Invalid or expired OTP. Please try again.',
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle forgot password
    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!forgotPasswordEmail) {
            toast.error('Please enter your email');
            return;
        }

        setIsLoading(true);
        try {
            await authAPI.forgotPassword(forgotPasswordEmail);
            
            toast.success('OTP Sent!', {
                description: 'Check your email for the 8-digit verification code.',
                duration: 5000,
            });
            
            setOtpData({ 
                email: forgotPasswordEmail,
                otp: ['', '', '', '', '', '', '', ''],
                resetToken: ''
            });
            setView('verify-forgot-otp');
        } catch (error: any) {
            toast.error('Error', {
                description: error.message || 'Could not send OTP.',
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle OTP input (8 digits)
    const handleOtpChange = (index: number, value: string) => {
        if (value.length > 1) return;
        if (value && !/^\d$/.test(value)) return; // Only allow digits
        
        const newOtp = [...otpData.otp];
        newOtp[index] = value;
        setOtpData({ ...otpData, otp: newOtp });

        // Auto-focus next input
        if (value && index < 7) {
            const nextInput = document.getElementById(`otp-${index + 1}`);
            nextInput?.focus();
        }
    };

    // Handle OTP paste (8 digits)
    const handleOtpPaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 8);
        const newOtp = pastedData.split('').concat(Array(8).fill('')).slice(0, 8);
        setOtpData({ ...otpData, otp: newOtp });
        
        // Focus last filled input
        const lastIndex = Math.min(pastedData.length - 1, 7);
        const lastInput = document.getElementById(`otp-${lastIndex}`);
        lastInput?.focus();
    };

    // Handle OTP backspace
    const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !otpData.otp[index] && index > 0) {
            const prevInput = document.getElementById(`otp-${index - 1}`);
            prevInput?.focus();
        }
    };

    // Handle verify forgot password OTP
    const handleVerifyForgotOTP = async (e: React.FormEvent) => {
        e.preventDefault();
        
        const otp = otpData.otp.join('');
        if (otp.length !== 8) {
            toast.error('Please enter complete 8-digit OTP');
            return;
        }

        setIsLoading(true);
        try {
            const response = await authAPI.verifyForgotPasswordOTP(otpData.email, otp);
            
            toast.success('OTP Verified!', {
                description: 'You can now reset your password.',
            });
            
            setOtpData({ ...otpData, resetToken: response.resetToken });
            setView('reset-password');
        } catch (error: any) {
            toast.error('Invalid OTP', {
                description: error.message || 'Please check your code and try again.',
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle reset password
    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!resetPasswordData.password || !resetPasswordData.confirmPassword) {
            toast.error('Please fill in all fields');
            return;
        }

        if (resetPasswordData.password !== resetPasswordData.confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (resetPasswordData.password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }

        setIsLoading(true);
        try {
            await authAPI.resetPassword(
                otpData.resetToken, 
                resetPasswordData.password,
                resetPasswordData.confirmPassword
            );
            
            toast.success('Password Reset Successful!', {
                description: 'You can now login with your new password.',
            });
            
            // Clear data and go to login
            setResetPasswordData({ password: '', confirmPassword: '' });
            setOtpData({
                email: '',
                otp: ['', '', '', '', '', '', '', ''],
                resetToken: ''
            });
            setView('login');
        } catch (error: any) {
            toast.error('Reset Failed', {
                description: error.message || 'Could not reset password.',
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4" style={{
            background: 'linear-gradient(135deg, #0A0A0A 0%, #1a1a2e 50%, #0A0A0A 100%)'
        }}>
            {/* Animated background */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute w-96 h-96 -top-48 -left-48 bg-cyan-500/10 rounded-full blur-3xl" />
                <div className="absolute w-96 h-96 -bottom-48 -right-48 bg-purple-500/10 rounded-full blur-3xl" />
            </div>

            <div className="w-full max-w-md relative z-10">
                <AnimatePresence mode="wait">
                    {/* Login View */}
                    {view === 'login' && (
                        <motion.div
                            key="login"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-8 shadow-2xl"
                        >
                            <div className="text-center mb-6">
                                <Logo size="md" />
                                <h1 className="mt-4 text-white text-xl">Admin Login</h1>
                                <p className="text-gray-400 mt-1.5 text-sm text-center">Sign in to access the dashboard</p>
                            </div>

                            <form onSubmit={handleLogin} className="space-y-6">
                                <div>
                                    <Label htmlFor="email" className="text-gray-300">Email</Label>
                                    <div className="relative mt-2">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-cyan-400" />
                                        <Input
                                            id="email"
                                            type="email"
                                            value={loginData.email}
                                            onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                                            className="pl-10 bg-gray-800/50 border-cyan-500/30 text-white focus:border-cyan-400"
                                            placeholder="admin@amasqis.ai"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <Label htmlFor="password" className="text-gray-300">Password</Label>
                                    <div className="relative mt-2">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-cyan-400" />
                                        <Input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            value={loginData.password}
                                            onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                                            className="pl-10 pr-10 bg-gray-800/50 border-cyan-500/30 text-white focus:border-cyan-400"
                                            placeholder="Password"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-cyan-400"
                                        >
                                            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                        </button>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between">
                                    <button
                                        type="button"
                                        onClick={() => setView('forgot-password')}
                                        className="text-sm text-cyan-400 hover:text-cyan-300"
                                    >
                                        Forgot password?
                                    </button>
                                </div>

                                <Button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full bg-linear-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white"
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Signing in...
                                        </>
                                    ) : (
                                        <>
                                            Sign In
                                            <ArrowRight className="ml-2 h-4 w-4" />
                                        </>
                                    )}
                                </Button>

                                <div className="text-center mt-6">
                                    <button
                                        type="button"
                                        onClick={() => setView('register')}
                                        className="text-sm text-gray-400 hover:text-cyan-400"
                                    >
                                        Don't have an account? <span className="text-cyan-400">Register</span>
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    )}

                    {/* Register View - Single Page with OTP */}
                    {view === 'register' && (
                        <motion.div
                            key="register"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-8 shadow-2xl"
                        >
                            <button
                                onClick={() => {
                                    setView('login');
                                    setRegistrationOTPState({ otpSent: false, otp: '', email: '' });
                                }}
                                className="mb-4 text-gray-400 hover:text-cyan-400 flex items-center"
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Back to login
                            </button>

                            <div className="text-center mb-6">
                                <Logo size="md" />
                                <h1 className="mt-4 text-white text-xl">Admin Registration</h1>
                                <p className="text-gray-400 mt-1.5 text-sm text-center">Request admin access</p>
                            </div>


                            <div className="space-y-6">
                                {/* Registration Form */}
                                <div>
                                    <Label htmlFor="reg-name" className="text-gray-300">Full Name</Label>
                                    <div className="relative mt-2">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-cyan-400" />
                                        <Input
                                            id="reg-name"
                                            type="text"
                                            value={registerData.name}
                                            onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                                            className="pl-10 bg-gray-800/50 border-cyan-500/30 text-white focus:border-cyan-400"
                                            placeholder="John Doe"
                                            disabled={registrationOTPState.otpSent}
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <Label htmlFor="reg-email" className="text-gray-300">Email</Label>
                                    <div className="relative mt-2">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-cyan-400" />
                                        <Input
                                            id="reg-email"
                                            type="email"
                                            value={registerData.email}
                                            onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                                            className="pl-10 bg-gray-800/50 border-cyan-500/30 text-white focus:border-cyan-400"
                                            placeholder="admin@amasqis.ai"
                                            disabled={registrationOTPState.otpSent}
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <Label htmlFor="reg-password" className="text-gray-300">Password</Label>
                                    <div className="relative mt-2">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-cyan-400" />
                                        <Input
                                            id="reg-password"
                                            type={showPassword ? 'text' : 'password'}
                                            value={registerData.password}
                                            onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                                            className="pl-10 pr-10 bg-gray-800/50 border-cyan-500/30 text-white focus:border-cyan-400"
                                            placeholder="Password"
                                            disabled={registrationOTPState.otpSent}
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-cyan-400"
                                        >
                                            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">Min 8 characters, uppercase, lowercase, number, special char</p>
                                </div>

                                <div>
                                    <Label htmlFor="reg-confirm-password" className="text-gray-300">Confirm Password</Label>
                                    <div className="relative mt-2">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-cyan-400" />
                                        <Input
                                            id="reg-confirm-password"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            value={registerData.confirmPassword}
                                            onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                                            className="pl-10 pr-10 bg-gray-800/50 border-cyan-500/30 text-white focus:border-cyan-400"
                                            placeholder="Password"
                                            disabled={registrationOTPState.otpSent}
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-cyan-400"
                                        >
                                            {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                        </button>
                                    </div>
                                </div>

                                {/* Send OTP Button */}
                                {!registrationOTPState.otpSent && (
                                    <Button
                                        type="button"
                                        onClick={handleSendRegistrationOTP}
                                        disabled={isLoading}
                                        className="w-full bg-linear-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white"
                                    >
                                        {isLoading ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Sending OTP...
                                            </>
                                        ) : (
                                            <>
                                                <Send className="mr-2 h-4 w-4" />
                                                Send OTP
                                            </>
                                        )}
                                    </Button>
                                )}

                                {/* OTP Section - Shown after OTP sent */}
                                {registrationOTPState.otpSent && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        className="space-y-4"
                                    >
                                        <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                                            <div className="flex items-start space-x-3">
                                                <Clock className="h-5 w-5 text-purple-400 mt-0.5 shrink-0" />
                                                <div>
                                                    <p className="text-sm text-purple-300">
                                                        <strong>OTP sent to Super Admin!</strong>
                                                    </p>
                                                    <p className="text-xs text-purple-300/80 mt-1">
                                                        Please contact the Super Admin to obtain the 8-digit OTP and enter it below. The OTP is valid for 10 minutes.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <Label htmlFor="reg-otp" className="text-gray-300">Enter 8-Digit OTP</Label>
                                            <div className="relative mt-2">
                                                <Input
                                                    id="reg-otp"
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength={8}
                                                    value={registrationOTPState.otp}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/\D/g, '');
                                                        setRegistrationOTPState({ ...registrationOTPState, otp: value });
                                                    }}
                                                    className="bg-gray-800/50 border-cyan-500/30 text-white text-center text-lg tracking-widest focus:border-cyan-400"
                                                    placeholder="--------"
                                                    required
                                                />
                                            </div>
                                            <p className="text-xs text-gray-400 mt-1">Enter the 8-digit OTP provided by Super Admin</p>
                                        </div>

                                        <div className="flex gap-3">
                                            <Button
                                                type="button"
                                                onClick={handleVerifyRegistrationOTP}
                                                disabled={isLoading || registrationOTPState.otp.length !== 8}
                                                className="flex-1 bg-linear-to-r from-purple-500 to-cyan-600 hover:from-purple-600 hover:to-cyan-700 text-white"
                                            >
                                                {isLoading ? (
                                                    <>
                                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                        Verifying...
                                                    </>
                                                ) : (
                                                    <>
                                                        <CheckCircle className="mr-2 h-4 w-4" />
                                                        Verify OTP
                                                    </>
                                                )}
                                            </Button>

                                            <Button
                                                type="button"
                                                onClick={handleResendRegistrationOTP}
                                                disabled={isLoading}
                                                variant="outline"
                                                className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                                            >
                                                {isLoading ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    'Resend OTP'
                                                )}
                                            </Button>
                                        </div>
                                    </motion.div>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {/* Forgot Password View */}
                    {view === 'forgot-password' && (
                        <motion.div
                            key="forgot-password"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-8 shadow-2xl"
                        >
                            <button
                                onClick={() => setView('login')}
                                className="mb-4 text-gray-400 hover:text-cyan-400 flex items-center"
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Back to login
                            </button>

                            <div className="text-center mb-6">
                                <Logo size="md" />
                                <h1 className="mt-4 text-white text-xl">Forgot Password</h1>
                                <p className="text-gray-400 mt-1.5 text-sm text-center">Enter your email to receive an OTP</p>
                            </div>

                            <form onSubmit={handleForgotPassword} className="space-y-6">
                                <div>
                                    <Label htmlFor="forgot-email" className="text-gray-300">Email</Label>
                                    <div className="relative mt-2">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-cyan-400" />
                                        <Input
                                            id="forgot-email"
                                            type="email"
                                            value={forgotPasswordEmail}
                                            onChange={(e) => setForgotPasswordEmail(e.target.value)}
                                            className="pl-10 bg-gray-800/50 border-cyan-500/30 text-white focus:border-cyan-400"
                                            placeholder="admin@amasqis.ai"
                                            required
                                        />
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full bg-linear-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white"
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Sending OTP...
                                        </>
                                    ) : (
                                        <>
                                            Send OTP
                                            <ArrowRight className="ml-2 h-4 w-4" />
                                        </>
                                    )}
                                </Button>
                            </form>
                        </motion.div>
                    )}

                    {/* Verify Forgot Password OTP View */}
                    {view === 'verify-forgot-otp' && (
                        <motion.div
                            key="verify-forgot-otp"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-8 shadow-2xl"
                        >
                            <button
                                onClick={() => setView('forgot-password')}
                                className="mb-4 text-gray-400 hover:text-cyan-400 flex items-center"
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Back
                            </button>

                            <div className="text-center mb-6">
                                <div className="mx-auto w-12 h-12 bg-cyan-500/20 rounded-full flex items-center justify-center mb-4">
                                    <Shield className="h-6 w-6 text-cyan-400" />
                                </div>
                                <h1 className="text-white text-xl">Verify OTP</h1>
                                <p className="text-gray-400 text-center mt-1.5 text-sm">Enter the 8-digit code sent to your email</p>
                                <p className="text-cyan-400 text-center text-sm mt-1">{otpData.email}</p>
                            </div>

                            <form onSubmit={handleVerifyForgotOTP} className="space-y-6">
                                <div>
                                    <Label className="text-gray-300">Enter 8-Digit OTP</Label>
                                    <div className="flex gap-2 mt-2" onPaste={handleOtpPaste}>
                                        {otpData.otp.map((digit, index) => (
                                            <Input
                                                key={index}
                                                id={`otp-${index}`}
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={1}
                                                value={digit}
                                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                className="w-12 h-12 text-center bg-gray-800/50 border-cyan-500/30 text-white text-lg focus:border-cyan-400"
                                                required
                                            />
                                        ))}
                                    </div>
                                    <p className="text-xs text-gray-400 mt-2">OTP expires in 10 minutes</p>
                                </div>

                                <Button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full bg-linear-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white"
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Verifying...
                                        </>
                                    ) : (
                                        <>
                                            Verify OTP
                                            <CheckCircle className="ml-2 h-4 w-4" />
                                        </>
                                    )}
                                </Button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setView('forgot-password');
                                        toast.info('Enter your email again to resend OTP');
                                    }}
                                    className="w-full text-sm text-gray-400 hover:text-cyan-400"
                                >
                                    Didn't receive OTP? Resend
                                </button>
                            </form>
                        </motion.div>
                    )}

                    {/* Reset Password View */}
                    {view === 'reset-password' && (
                        <motion.div
                            key="reset-password"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-8 shadow-2xl"
                        >
                            <div className="text-center mb-6">
                                <Logo size="md" />
                                <h1 className="mt-4 text-white text-xl">Reset Password</h1>
                                <p className="text-gray-400 text-center mt-1.5 text-sm">Create a new password</p>
                            </div>

                            <form onSubmit={handleResetPassword} className="space-y-6">
                                <div>
                                    <Label htmlFor="new-password" className="text-gray-300">New Password</Label>
                                    <div className="relative mt-2">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-cyan-400" />
                                        <Input
                                            id="new-password"
                                            type={showPassword ? 'text' : 'password'}
                                            value={resetPasswordData.password}
                                            onChange={(e) => setResetPasswordData({ ...resetPasswordData, password: e.target.value })}
                                            className="pl-10 pr-10 bg-gray-800/50 border-cyan-500/30 text-white focus:border-cyan-400"
                                            placeholder="Password"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-cyan-400"
                                        >
                                            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">Min 8 characters, uppercase, lowercase, number, special char</p>
                                </div>

                                <div>
                                    <Label htmlFor="confirm-new-password" className="text-gray-300">Confirm Password</Label>
                                    <div className="relative mt-2">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-cyan-400" />
                                        <Input
                                            id="confirm-new-password"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            value={resetPasswordData.confirmPassword}
                                            onChange={(e) => setResetPasswordData({ ...resetPasswordData, confirmPassword: e.target.value })}
                                            className="pl-10 pr-10 bg-gray-800/50 border-cyan-500/30 text-white focus:border-cyan-400"
                                            placeholder="Password"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-cyan-400"
                                        >
                                            {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                        </button>
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full bg-linear-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white"
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Resetting...
                                        </>
                                    ) : (
                                        <>
                                            Reset Password
                                            <CheckCircle className="ml-2 h-4 w-4" />
                                        </>
                                    )}
                                </Button>
                            </form>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Footer */}
                <div className="text-center mt-8">
                    <Link to="/" className="text-gray-400 hover:text-cyan-400 text-sm">
                        ← Back to Website
                    </Link>
                </div>
            </div>
        </div>
    );
}
